import React, { useEffect, useState } from "react";
import { Box, Paper, Typography, Grid, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import i18next from "i18next";
import toast from "react-hot-toast";
import { useCreateProject } from "../../hooks/projects/useCreateProject";
import { useUpdateProject } from "../../hooks/projects/useUpdateProject";
import { useGetClientsForSelect } from "../../hooks/clients/useGetClientsForSelect";
import { useGetServices } from "../../hooks/services/useGetServices";
import FormInput from "../inputs/FormInput";
import TextEditor from "../inputs/TextEditor";
import LanguageToggle from "../global/LanguageToggle";
import { slugify } from "../../utils/slugify";

const FormAddEditProject = ({ currentData, isEdit = false, onSubmitSuccess }) => {
    const { i18n } = useTranslation();
    const lng = i18n.language || "en";
    const isArabic = lng.startsWith("ar");
    const initialLang = localStorage.getItem("i18nextLng") || i18next.language || "ar";
    const [activeLang, setActiveLang] = useState(initialLang.startsWith("ar") ? "ar" : "en");
    const theme = useTheme();
    const isDark = theme.palette.mode === "dark";

    // Form States
    const [titleAr, setTitleAr] = useState("");
    const [titleEn, setTitleEn] = useState("");
    const [slug, setSlug] = useState("");
    const [descriptionAr, setDescriptionAr] = useState("");
    const [descriptionEn, setDescriptionEn] = useState("");
    const [clientId, setClientId] = useState("");
    const [serviceId, setServiceId] = useState("");
    const [showInHome, setShowInHome] = useState(false);
    const [sortOrder, setSortOrder] = useState("");
    const [mainImage, setMainImage] = useState(null);
    const [gallery, setGallery] = useState([]);
    const [reals, setReals] = useState("");

    // Hooks
    const { createProjectMutate, isPending: isCreating } = useCreateProject();
    const { updateProjectMutate, isPending: isUpdating } = useUpdateProject();
    const { options: clientOptions, isLoading: loadingClients } = useGetClientsForSelect();
    const { services: serviceOptions, isLoading: loadingServices } = useGetServices({ per_page: 1000 });

    const loadingSubmit = isCreating || isUpdating;

    // Auto-slug generation from English title (only in English mode)
    useEffect(() => {
        if (activeLang === "en" && titleEn && (!isEdit || !slug)) {
            setSlug(slugify(titleEn));
        }
    }, [titleEn, activeLang, isEdit, slug]);

    // Fill form data in edit mode
    useEffect(() => {
        if (isEdit && currentData) {
            setTitleAr(currentData?.title?.ar || currentData?.title || "");
            setTitleEn(currentData?.title?.en || currentData?.title || "");
            setSlug(currentData?.slug || "");
            setDescriptionAr(currentData?.description?.ar || currentData?.description || "");
            setDescriptionEn(currentData?.description?.en || currentData?.description || "");
            setClientId(currentData?.client_id || currentData?.client?.id || "");
            setServiceId(currentData?.service_id || currentData?.service?.id || "");
            setShowInHome(currentData?.show_in_home || false);
            setSortOrder(currentData?.sort_order || "");
            setMainImage(currentData?.main_image || null);
            setGallery(currentData?.gallery || []);
            setReals(currentData?.reals?.join("\n") || "");
        }
    }, [isEdit, currentData]);

    const handleSlugChange = (e) => {
        const val = e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, "");
        setSlug(val);
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (!slug) {
            toast.error(isArabic ? "يرجى إدخال الرابط (Slug)" : "Please enter a slug");
            return;
        }

        const formData = new FormData();
        formData.append("title[en]", titleEn || titleAr);
        formData.append("title[ar]", titleAr || titleEn);
        formData.append("slug", slug);
        formData.append("description[en]", descriptionEn || descriptionAr);
        formData.append("description[ar]", descriptionAr || descriptionEn);
        formData.append("service_id", serviceId || "");
        formData.append("client_id", clientId || "");
        formData.append("show_in_home", showInHome ? "1" : "0");
        formData.append("sort_order", sortOrder || "0");

        // reals (array of URLs)
        const realsArray = reals
            .split("\n")
            .map((r) => r.trim())
            .filter((r) => r.length > 0);
            
        if (realsArray.length > 0) {
            realsArray.forEach((url, index) => {
                formData.append(`reals[${index}]`, url);
            });
        } else {
            formData.append(`reals`, "");
        }

        if (isEdit) {
            formData.append("_method", "PUT");
        }

        // main_image (single) — extract filename from URL, InputFileUpload result, or string
        const getImageFilename = (val) => {
            if (!val) return '';
            if (val?.target?.value) return val.target.value;
            if (typeof val === 'string') return val.split('/').pop();
            return '';
        };
        const mainImageValue = getImageFilename(mainImage);
        if (mainImageValue) {
            formData.append("main_image", mainImageValue);
        }

        // gallery (array of filenames) — normalize {file_name, name, string} to filename strings
        const galleryFilenames = Array.isArray(gallery)
            ? gallery.filter(g => g != null).map(g => {
                if (typeof g === 'string') return g.split('/').pop();
                if (g?.file_name) return g.file_name;
                if (g?.name) return g.name;
                if (g?.file?.name) return g.file.name;
                return '';
            }).filter(Boolean)
            : [];
            
        if (galleryFilenames.length > 0) {
            galleryFilenames.forEach((name, index) => {
                formData.append(`gallery[${index}]`, name);
            });
        } else {
            formData.append(`gallery`, "");
        }


        const options = {
            onSuccess: () => {
                if (onSubmitSuccess) onSubmitSuccess();
            }
        };

        if (isEdit) {
            updateProjectMutate({ id: currentData.id, data: formData }, options);
        } else {
            createProjectMutate(formData, options);
        }
    };

    return (
        <Paper
            sx={{
                p: 3,
                mt: 2,
                direction: isArabic ? "rtl" : "ltr",
                backgroundColor: isDark ? "transparent" : "#FFFFFF",
            }}
        >
            <Box component="form" onSubmit={handleSubmit} sx={{ p: { xs: 2, md: 4 }, display: "flex", flexDirection: "column", gap: 3 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 2, flexWrap: "wrap", gap: 2 }}>
                    <Typography variant="h6">
                        {isEdit ? (isArabic ? "تعديل المشروع" : "Edit Project") : (isArabic ? "إضافة مشروع جديد" : "Add New Project")}
                    </Typography>
                    <LanguageToggle activeLang={activeLang} onChange={(l) => setActiveLang(l)} showLabel={true} />
                </Box>

                <Grid container spacing={3}>
                    {/* Main Image */}
                    <Grid item xs={12} sx={{ display: 'flex', justifyContent: 'center' }}>
                        <FormInput
                            label={isArabic ? "الصورة الرئيسية" : "Main Image"}
                            type="file"
                            name="main_image"
                            value={mainImage}
                            onChange={(file) => setMainImage(file)}
                        />
                    </Grid>

                    {/* Gallery Images */}
                    <Grid item xs={12}>
                        <FormInput
                            label={isArabic ? "معرض الصور" : "Gallery"}
                            type="multi-image"
                            name="gallery"
                            value={gallery}
                            onChange={setGallery}
                            maxImages={20}
                        />
                    </Grid>

                    {/* Multilingual Titles */}
                    {activeLang === "ar" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="عنوان المشروع (عربي)"
                                placeholder="أدخل عنوان المشروع بالعربي"
                                value={titleAr}
                                onChange={(e) => setTitleAr(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12} md={6}>
                            <FormInput
                                label="Project Title (English)"
                                placeholder="Enter project title in English"
                                value={titleEn}
                                onChange={(e) => setTitleEn(e.target.value)}
                                required
                                fullWidth
                            />
                        </Grid>
                    )}

                    {/* Slug */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الرابط (Slug - إنجليزي فقط)" : "Slug (English only)"}
                            placeholder="project-name"
                            type="text"
                            value={slug}
                            onChange={handleSlugChange}
                            disabled={activeLang === "en"}
                            required
                            helperText={activeLang === "ar" ? (isArabic ? "يجب إدخال الرابط باللغة الإنجليزية" : "Slug must be entered in English") : ""}
                        />
                    </Grid>

                    {/* Client Select */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "العميل" : "Client"}
                            type="select"
                            name="client_id"
                            value={clientId}
                            onChange={(e) => setClientId(e.target.value)}
                            options={clientOptions.map(client => ({
                                id: client.id,
                                name: isArabic
                                    ? (client.title?.ar || client.title?.en || Object.values(client.title || {})[0])
                                    : (client.title?.en || client.title?.ar || Object.values(client.title || {})[0])
                            }))}
                            fullWidth
                            loading={loadingClients}
                        />
                    </Grid>

                    {/* Service Select */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "الخدمة" : "Service"}
                            type="select"
                            name="service_id"
                            value={serviceId}
                            onChange={(e) => setServiceId(e.target.value)}
                            options={serviceOptions.map(service => ({
                                id: service.id,
                                name: isArabic
                                    ? (service.title?.ar || service.title?.en || service.title || "")
                                    : (service.title?.en || service.title?.ar || service.title || "")
                            }))}
                            fullWidth
                            loading={loadingServices}
                        />
                    </Grid>

                    {/* Show in Home Switch */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "عرض في الرئيسية" : "Show in Home"}
                            type="switch"
                            name="show_in_home"
                            checked={showInHome}
                            onChange={(e) => setShowInHome(e.target.checked)}
                        />
                    </Grid>

                    {/* Sort Order */}
                    <Grid item xs={12} md={6}>
                        <FormInput
                            label={isArabic ? "ترتيب" : "Sort Order"}
                            type="number"
                            name="sort_order"
                            value={sortOrder}
                            onChange={(e) => setSortOrder(e.target.value)}
                            fullWidth
                        />
                    </Grid>

                    {/* Reals (URLs) */}
                    <Grid item xs={12}>
                        <FormInput
                            label={isArabic ? "روابط (كل رابط في سطر)" : "URLs (one per line)"}
                            type="textarea"
                            name="reals"
                            value={reals}
                            onChange={(e) => setReals(e.target.value)}
                            placeholder={isArabic ? "https://example1.com\nhttps://example2.com" : "https://example1.com\nhttps://example2.com"}
                            textareaRows="100px"
                            fullWidth
                            required={false}
                        />
                    </Grid>

                    {/* Multilingual Descriptions */}
                    {activeLang === "ar" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                وصف المشروع (عربي)
                            </Typography>
                            <TextEditor
                                name="description_ar"
                                value={descriptionAr}
                                onChange={(e) => setDescriptionAr(e.target.value)}
                                placeholder="أدخل وصف المشروع بالعربي"
                            />
                        </Grid>
                    )}

                    {activeLang === "en" && (
                        <Grid item xs={12}>
                            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 500 }}>
                                Project Description (English)
                            </Typography>
                            <TextEditor
                                name="description_en"
                                value={descriptionEn}
                                onChange={(e) => setDescriptionEn(e.target.value)}
                                placeholder="Enter project description in English"
                            />
                        </Grid>
                    )}
                </Grid>

                <Box mt={4} display="flex" justifyContent="flex-end" gap={2}>
                    <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={loadingSubmit}
                        sx={{ minWidth: 120 }}
                    >
                        {loadingSubmit ? (isArabic ? "جارٍ الحفظ..." : "Saving...") : (isEdit ? (isArabic ? "تحديث" : "Update") : (isArabic ? "حفظ" : "Save"))}
                    </Button>
                </Box>
            </Box>
        </Paper>
    );
};

export default FormAddEditProject;
